import React, { useEffect, useMemo, useState } from "react";
import { Accordion, Button, Card, Form, InputGroup, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../../../services/api";
import {
  deleteHotelRoomCategoryImage,
  uploadHotelPaymentScanner,
  uploadHotelRoomCategoryImages,
} from "../../../services/hotelMasterApi";

const ADD_NEW_CITY = "__add_new_city__";

// Matches spring.servlet.multipart.max-file-size on the backend. Checked here so an
// oversized video is rejected before it is uploaded, not after.
const MAX_ROOM_MEDIA_BYTES = 100 * 1024 * 1024;

const formatFileSize = (bytes) =>
  `${(Number(bytes || 0) / (1024 * 1024)).toFixed(1)} MB`;

const blankHotel = {
  hotelName: "",
  hotelCode: "",
  city: "",
  googleMapLink: "",
  caretakerNumber: "",
  caretakerName: "",
  ownerName: "",
  ownerNumber: "",
  facilities: [],
  upiNumber: "",
  upiScannerUrl: "",
  bankAccountHolder: "",
  bankAccountNumber: "",
  bankIfsc: "",
  bankName: "",
  bankBranch: "",
  displayOrder: 0,
  active: true,
  stayType: "",
  iotripsPick: false,
  minGuestsPerRoom: false,
  roomCategories: [],
};

const blankCategory = {
  categoryName: "",
  pricingType: "PER_ROOM",
  perPersonPrice: 0,
  extraMattressPayable: 0,
  // Customer prices start blank: the backend then charges the hotel payable.
  extraMattressPrice: null,
  kidRoomCountAgeThreshold: 3,
  kidGracePerRoom: 2,
  maximumUnits: 0,
  displayOrder: 0,
  active: true,
  priceOptions: [],
  images: [],
  rooms: [],
};

const activeRoomCount = (category) =>
  (category?.rooms || []).filter((room) => room?.active !== false && String(room?.roomNumber || "").trim()).length;

const blankRoomUnit = {
  roomNumber: "",
  displayOrder: 0,
  active: true,
  note: "",
};

// cost is the customer price quotations charge; hotelPayable is what the hotel is paid.
const blankPriceOption = {
  memberCount: 2,
  hotelPayable: 0,
  cost: null,
  displayOrder: 0,
};

const blankSpecialPrice = {
  fromDate: "",
  toDate: "",
  recurrenceType: "DATE_RANGE",
  dayOfWeekMask: 0,
  memberCount: "",
  hotelPayable: 0,
  cost: null,
  note: "",
  active: true,
};

const weekDays = [
  { label: "Mon", bit: 1 },
  { label: "Tue", bit: 2 },
  { label: "Wed", bit: 4 },
  { label: "Thu", bit: 8 },
  { label: "Fri", bit: 16 },
  { label: "Sat", bit: 32 },
  { label: "Sun", bit: 64 },
];

const blankFacility = { name: "", displayOrder: 0, active: true };

const blankStayType = { name: "", description: "", displayOrder: 0, active: true };

// With "Minimum guests per room" on, a room's minimum is the smallest group it is priced for - read
// from the prices being edited, so it follows them before the hotel is saved.
const minGuestsFor = (category) => {
  if (category?.pricingType === "PER_PERSON") return 0;
  const counts = (category?.priceOptions || []).map((option) => Number(option.memberCount || 0)).filter((count) => count > 0);
  return counts.length ? Math.min(...counts) : 0;
};

// Hotels store the stay type name itself, which is also the column's limit.
const STAY_TYPE_NAME_MAX = 40;
const STAY_TYPE_DESCRIPTION_MAX = 255;

const rupees = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;

const isBlankAmount = (value) => value === null || value === undefined || value === "";

// What the hotel is paid. Rows saved before the split carry only cost, which the hotel was paid.
const payableOf = (payable, customerPrice) => (isBlankAmount(payable) ? Number(customerPrice || 0) : Number(payable));

// A blank customer price is charged at the hotel payable, so it carries no margin.
const marginOf = (payable, customerPrice) => (isBlankAmount(customerPrice)
  ? 0
  : Number(customerPrice) - payableOf(payable, customerPrice));

const marginLabel = (payable, customerPrice) => {
  const margin = marginOf(payable, customerPrice);
  if (margin < 0) return "Below payable";
  return margin > 0 ? rupees(margin) : "None";
};

// Both prices for the list view; the customer price only when it differs from the payable.
const priceLabel = (payable, customerPrice) => {
  const hotelPaid = payableOf(payable, customerPrice);
  return marginOf(payable, customerPrice) > 0
    ? `Rs. ${hotelPaid} (customer Rs. ${customerPrice})`
    : `Rs. ${hotelPaid}`;
};

const todayIso = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

const formatCardDate = (isoDate) => {
  if (!isoDate) return "";
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const weekDayLabels = (mask) => weekDays
  .filter((day) => (Number(mask || 0) & day.bit) !== 0)
  .map((day) => day.label);

// How one special-price rule reads to the hotel. Rules are grouped by their period, so the guest
// counts under a single weekend or season appear together instead of as separate lines.
const specialPricePeriodLabel = (rule) => {
  if ((rule.recurrenceType || "DATE_RANGE") === "WEEKLY") {
    const labels = weekDayLabels(rule.dayOfWeekMask);
    return labels.length ? `Every ${labels.join(", ")}` : "Every day";
  }
  if (rule.fromDate && rule.toDate && rule.fromDate !== rule.toDate) {
    return `${formatCardDate(rule.fromDate)} to ${formatCardDate(rule.toDate)}`;
  }
  return formatCardDate(rule.fromDate || rule.toDate) || "Selected dates";
};

// A rate card answers "what do you have on file for us?". It carries only what was agreed with the
// hotel - never how many rooms we hold with them, and never what the guest pays us, so every rate
// on it is the hotel payable, not the customer price.
const buildRateCardText = (hotel) => {
  const categories = (hotel.roomCategories || []).filter((category) => category.active !== false);
  if (!categories.length) return null;

  const hotelLabel = (hotel.hotelName || "").trim() || (hotel.hotelCode || "").trim() || "Hotel";
  const city = (hotel.city || "").trim();
  const today = todayIso();

  const lines = [
    "*IO Trips – Agreed Rate Card*",
    `*${hotelLabel}*${city ? `   📍 ${city}` : ""}`,
    `📅 As on ${formatCardDate(today)}`,
    "",
    "These are the rates we have on record for your property.",
    "",
  ];

  categories.forEach((category, index) => {
    lines.push(`*${index + 1}. ${category.categoryName || "Room"}*`);

    if (category.pricingType === "PER_PERSON") {
      lines.push(`   • ${rupees(category.perPersonPrice)} per person, per night`);
    } else {
      const options = [...(category.priceOptions || [])]
        .filter((option) => Number(option.memberCount || 0) > 0)
        .sort((a, b) => Number(a.memberCount || 0) - Number(b.memberCount || 0));
      if (options.length) {
        options.forEach((option) => {
          lines.push(`   • ${option.memberCount} guest${Number(option.memberCount) === 1 ? "" : "s"}: ${rupees(payableOf(option.hotelPayable, option.cost))} per night`);
        });
        // Read back to the hotel so they confirm the rule we book by.
        if (hotel.minGuestsPerRoom && minGuestsFor(category) > 0) {
          lines.push(`   • Minimum ${minGuestsFor(category)} guests per room`);
        }
      } else {
        lines.push("   • Rate not recorded - please confirm with us");
      }
    }

    const mattressPayable = payableOf(category.extraMattressPayable, category.extraMattressPrice);
    if (mattressPayable > 0) {
      lines.push(`   • Extra mattress: ${rupees(mattressPayable)} per night`);
    }

    const grace = Number(category.kidGracePerRoom ?? 0);
    const kidAge = Number(category.kidRoomCountAgeThreshold || 0);
    if (grace > 0 && kidAge > 0) {
      lines.push(`   • Children: up to ${grace} child${grace === 1 ? "" : "ren"} under ${kidAge} years at no extra charge`);
    }

    // Only what is still ahead of us. A season that finished last year is not a live rate and
    // only invites an argument.
    const activeSpecials = (category.specialPrices || []).filter((rule) => {
      if (rule.active === false) return false;
      if ((rule.recurrenceType || "DATE_RANGE") === "WEEKLY") return true;
      const end = rule.toDate || rule.fromDate;
      return !end || end >= today;
    });

    if (activeSpecials.length) {
      const groups = new Map();
      activeSpecials.forEach((rule) => {
        const key = [
          rule.recurrenceType || "DATE_RANGE",
          rule.dayOfWeekMask || 0,
          rule.fromDate || "",
          rule.toDate || "",
          (rule.note || "").trim(),
        ].join("|");
        if (!groups.has(key)) groups.set(key, { rule, entries: [] });
        groups.get(key).entries.push(rule);
      });
      lines.push("   *Special dates:*");
      Array.from(groups.values()).forEach((group) => {
        const period = specialPricePeriodLabel(group.rule);
        const note = (group.rule.note || "").trim();
        const priced = [...group.entries]
          .sort((a, b) => Number(a.memberCount || 0) - Number(b.memberCount || 0))
          .map((rule) => (Number(rule.memberCount || 0) > 0
            ? `${rule.memberCount} guest${Number(rule.memberCount) === 1 ? "" : "s"}: ${rupees(payableOf(rule.hotelPayable, rule.cost))}`
            : rupees(payableOf(rule.hotelPayable, rule.cost))))
          .join("  ·  ");
        lines.push(`      – ${period}${note ? ` (${note})` : ""}`);
        lines.push(`         ${priced}`);
      });
    }

    lines.push("");
  });

  lines.push("────────────────────");
  lines.push("All rates are per night and include what we have agreed with you.");
  lines.push("If anything here does not match your records, please call us before the next booking.");
  lines.push("");
  lines.push("Thank you!");
  lines.push("*IO Trips*");
  return lines.join("\n");
};

// WhatsApp wants digits only with a country code. Caretaker numbers are typed in by hand, so
// anything not recognisably an Indian mobile is left to a manual copy rather than dialled wrongly.
const rateCardWhatsappNumber = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 11 && digits.startsWith("0")) return `91${digits.slice(1)}`;
  if (digits.length === 12 && digits.startsWith("91")) return digits;
  return "";
};

const copyRateCardToClipboard = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
};

export default function HotelsManager() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(blankHotel);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scannerUploading, setScannerUploading] = useState(false);
  const [categoryImageUploading, setCategoryImageUploading] = useState({});
  const [search, setSearch] = useState("");
  const [facilities, setFacilities] = useState([]);
  const [facilityDraft, setFacilityDraft] = useState(blankFacility);
  const [stayTypes, setStayTypes] = useState([]);
  const [stayTypeDraft, setStayTypeDraft] = useState(blankStayType);
  const [externalVendors, setExternalVendors] = useState([]);
  const [addingCity, setAddingCity] = useState(false);

  const knownCities = useMemo(
    () => Array.from(new Set((rows || []).map((hotel) => String(hotel.city || "").trim()).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b)),
    [rows]
  );

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/admin/hotels");
      setRows(Array.isArray(data) ? data.map(normalizeHotel) : []);
      const facilityRes = await api.get("/api/admin/hotels/facilities");
      setFacilities(Array.isArray(facilityRes.data) ? facilityRes.data.map(normalizeFacility) : []);
      const stayTypeRes = await api.get("/api/admin/hotels/stay-types");
      setStayTypes(Array.isArray(stayTypeRes.data) ? stayTypeRes.data.map(normalizeStayType) : []);
      const vendorRes = await api.get("/api/admin/external-vendors", { params: { activeOnly: true } });
      setExternalVendors(Array.isArray(vendorRes.data) ? vendorRes.data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    setAddingCity(false);
  }, [editing.id]);

  const patch = (field, value) => {
    setEditing((prev) => ({ ...prev, [field]: value }));
  };

  const toggleHotelFacility = (facility) => {
    setEditing((prev) => {
      const selected = prev.facilities || [];
      const exists = selected.some((item) => item.id === facility.id);
      return {
        ...prev,
        facilities: exists
          ? selected.filter((item) => item.id !== facility.id)
          : [...selected, { ...facility, costBehavior: "INCLUDED_BY_HOTEL", pricingType: "PER_ACTIVITY", optionalWithCost: false, extraCost: 0, hotelPayable: 0, externalVendorId: "" }],
      };
    });
  };


  const patchHotelFacility = (facilityId, field, value) => {
    setEditing((prev) => ({
      ...prev,
      facilities: (prev.facilities || []).map((item) =>
        item.id === facilityId ? normalizeFacilityPatch(item, field, value) : item,
      ),
    }));
  };
  const saveFacility = async (facility = facilityDraft) => {
    if (!String(facility.name || "").trim()) {
      toast.error("Facility name is required");
      return;
    }
    if (facility.id) {
      await api.patch(`/api/admin/hotels/facilities/${facility.id}`, facility);
    } else {
      await api.post("/api/admin/hotels/facilities", facility);
      setFacilityDraft(blankFacility);
    }
    await load();
    toast.success("Facility saved");
  };

  const deleteFacility = async (facility) => {
    const used = rows.filter((hotel) => (hotel.facilities || []).some((item) => item.id === facility.id)).length;
    const warning = used ? `\n\nIt will be removed from ${used} hotel${used === 1 ? "" : "s"} that have it.` : "";
    if (!window.confirm(`Delete facility "${facility.name}"?${warning}`)) return;
    await api.delete(`/api/admin/hotels/facilities/${facility.id}`);
    await load();
    toast.success("Facility deleted");
  };

  const hotelsUsingStayType = (name) => {
    const key = String(name || "").trim().toLowerCase();
    return key ? rows.filter((hotel) => String(hotel.stayType || "").trim().toLowerCase() === key).length : 0;
  };

  const saveStayType = async (stayType = stayTypeDraft) => {
    if (!String(stayType.name || "").trim()) {
      toast.error("Stay type name is required");
      return;
    }
    if (stayType.id) {
      await api.patch(`/api/admin/hotels/stay-types/${stayType.id}`, stayType);
    } else {
      await api.post("/api/admin/hotels/stay-types", stayType);
      setStayTypeDraft(blankStayType);
    }
    await load();
    toast.success("Stay type saved");
  };

  const deleteStayType = async (stayType) => {
    const used = hotelsUsingStayType(stayType.name);
    const warning = used ? `\n\n${used} hotel${used === 1 ? "" : "s"} using it will be set to Not set.` : "";
    if (!window.confirm(`Delete stay type "${stayType.name}"?${warning}`)) return;
    await api.delete(`/api/admin/hotels/stay-types/${stayType.id}`);
    await load();
    toast.success("Stay type deleted");
  };

  const patchCategory = (index, field, value) => {
    setEditing((prev) => ({
      ...prev,
      roomCategories: (prev.roomCategories || []).map((category, idx) =>
        idx === index ? { ...category, [field]: value } : category,
      ),
    }));
  };

  const addCategory = () => {
    setEditing((prev) => ({
      ...prev,
      roomCategories: [
        ...(prev.roomCategories || []),
        {
          ...blankCategory,
          displayOrder: (prev.roomCategories || []).length + 1,
          priceOptions: [
            { ...blankPriceOption, memberCount: 2, displayOrder: 1 },
            { ...blankPriceOption, memberCount: 3, displayOrder: 2 },
          ],
        },
      ],
    }));
  };

  const removeCategory = (index) => {
    setEditing((prev) => ({
      ...prev,
      roomCategories: (prev.roomCategories || []).filter((_, idx) => idx !== index),
    }));
  };

  const addPriceOption = (categoryIndex) => {
    setEditing((prev) => ({
      ...prev,
      roomCategories: (prev.roomCategories || []).map((category, idx) => {
        if (idx !== categoryIndex) return category;
        const options = category.priceOptions || [];
        return {
          ...category,
          priceOptions: [
            ...options,
            {
              ...blankPriceOption,
              memberCount: options.length ? Number(options[options.length - 1].memberCount || 1) + 1 : 2,
              displayOrder: options.length + 1,
            },
          ],
        };
      }),
    }));
  };

  const patchPriceOption = (categoryIndex, optionIndex, field, value) => {
    setEditing((prev) => ({
      ...prev,
      roomCategories: (prev.roomCategories || []).map((category, idx) => {
        if (idx !== categoryIndex) return category;
        return {
          ...category,
          priceOptions: (category.priceOptions || []).map((option, oIdx) =>
            oIdx === optionIndex ? { ...option, [field]: value } : option,
          ),
        };
      }),
    }));
  };

  const removePriceOption = (categoryIndex, optionIndex) => {
    setEditing((prev) => ({
      ...prev,
      roomCategories: (prev.roomCategories || []).map((category, idx) => {
        if (idx !== categoryIndex) return category;
        return {
          ...category,
          priceOptions: (category.priceOptions || []).filter((_, oIdx) => oIdx !== optionIndex),
        };
      }),
    }));
  };

  const addRoomUnit = (categoryIndex) => {
    setEditing((prev) => ({
      ...prev,
      roomCategories: (prev.roomCategories || []).map((category, idx) => {
        if (idx !== categoryIndex) return category;
        const rooms = category.rooms || [];
        return {
          ...category,
          rooms: [...rooms, { ...blankRoomUnit, displayOrder: rooms.length + 1 }],
        };
      }),
    }));
  };

  const patchRoomUnit = (categoryIndex, roomIndex, field, value) => {
    setEditing((prev) => ({
      ...prev,
      roomCategories: (prev.roomCategories || []).map((category, idx) => {
        if (idx !== categoryIndex) return category;
        return {
          ...category,
          rooms: (category.rooms || []).map((room, rIdx) =>
            rIdx === roomIndex ? { ...room, [field]: value } : room,
          ),
        };
      }),
    }));
  };

  const removeRoomUnit = (categoryIndex, roomIndex) => {
    setEditing((prev) => ({
      ...prev,
      roomCategories: (prev.roomCategories || []).map((category, idx) => {
        if (idx !== categoryIndex) return category;
        return {
          ...category,
          rooms: (category.rooms || []).filter((_, rIdx) => rIdx !== roomIndex),
        };
      }),
    }));
  };

  // Preference order is what the office agreed with the hotel, so it has to be reorderable
  // without retyping every rank by hand.
  const moveRoomUnit = (categoryIndex, roomIndex, direction) => {
    setEditing((prev) => ({
      ...prev,
      roomCategories: (prev.roomCategories || []).map((category, idx) => {
        if (idx !== categoryIndex) return category;
        const rooms = [...(category.rooms || [])];
        const target = roomIndex + direction;
        if (target < 0 || target >= rooms.length) return category;
        [rooms[roomIndex], rooms[target]] = [rooms[target], rooms[roomIndex]];
        return {
          ...category,
          rooms: rooms.map((room, rIdx) => ({ ...room, displayOrder: rIdx + 1 })),
        };
      }),
    }));
  };

  const addSpecialPrice = (categoryIndex) => {
    setEditing((prev) => ({
      ...prev,
      roomCategories: (prev.roomCategories || []).map((category, idx) =>
        idx === categoryIndex
          ? {
              ...category,
              specialPrices: [
                ...(category.specialPrices || []),
                {
                  ...blankSpecialPrice,
                  memberCount: category.pricingType === "PER_PERSON" ? "" : 2,
                },
              ],
            }
          : category,
      ),
    }));
  };

  const patchSpecialPrice = (categoryIndex, rowIndex, field, value) => {
    setEditing((prev) => ({
      ...prev,
      roomCategories: (prev.roomCategories || []).map((category, idx) => {
        if (idx !== categoryIndex) return category;
        return {
          ...category,
          specialPrices: (category.specialPrices || []).map((row, rIdx) =>
            rIdx === rowIndex ? { ...row, [field]: value } : row,
          ),
        };
      }),
    }));
  };

  const removeSpecialPrice = (categoryIndex, rowIndex) => {
    setEditing((prev) => ({
      ...prev,
      roomCategories: (prev.roomCategories || []).map((category, idx) => {
        if (idx !== categoryIndex) return category;
        return {
          ...category,
          specialPrices: (category.specialPrices || []).filter((_, rIdx) => rIdx !== rowIndex),
        };
      }),
    }));
  };

  const patchSpecialPriceGroup = (categoryIndex, group, field, value) => {
    const rowIndexes = new Set(group.rowIndexes || []);
    setEditing((prev) => ({
      ...prev,
      roomCategories: (prev.roomCategories || []).map((category, idx) => {
        if (idx !== categoryIndex) return category;
        return {
          ...category,
          specialPrices: (category.specialPrices || []).map((row, rIdx) =>
            rowIndexes.has(rIdx) ? { ...row, [field]: value } : row,
          ),
        };
      }),
    }));
  };

  const toggleSpecialPriceGroupWeekday = (categoryIndex, group, bit) => {
    const nextMask = Number(group.rule.dayOfWeekMask || 0) & bit
      ? Number(group.rule.dayOfWeekMask || 0) & ~bit
      : Number(group.rule.dayOfWeekMask || 0) | bit;
    patchSpecialPriceGroup(categoryIndex, group, "dayOfWeekMask", nextMask);
  };

  const addSpecialMemberPrice = (categoryIndex, group, pricingType) => {
    setEditing((prev) => ({
      ...prev,
      roomCategories: (prev.roomCategories || []).map((category, idx) => {
        if (idx !== categoryIndex) return category;
        const existingCounts = (group.prices || [])
          .map((item) => Number(item.row.memberCount || 0))
          .filter((value) => value > 0);
        const nextMemberCount = pricingType === "PER_PERSON"
          ? ""
          : existingCounts.length
            ? Math.max(...existingCounts) + 1
            : 2;
        return {
          ...category,
          specialPrices: [
            ...(category.specialPrices || []),
            {
              ...blankSpecialPrice,
              ...group.rule,
              memberCount: nextMemberCount,
              hotelPayable: 0,
              cost: null,
            },
          ],
        };
      }),
    }));
  };

  const patchSpecialMemberPrice = (categoryIndex, rowIndex, field, value) => {
    patchSpecialPrice(categoryIndex, rowIndex, field, value);
  };

  const removeSpecialPriceGroup = (categoryIndex, group) => {
    const rowIndexes = new Set(group.rowIndexes || []);
    setEditing((prev) => ({
      ...prev,
      roomCategories: (prev.roomCategories || []).map((category, idx) => {
        if (idx !== categoryIndex) return category;
        return {
          ...category,
          specialPrices: (category.specialPrices || []).filter((_, rIdx) => !rowIndexes.has(rIdx)),
        };
      }),
    }));
  };

  const setCategoryImages = (categoryId, images) => {
    const normalizedImages = Array.isArray(images) ? images.map(normalizeRoomImage) : [];
    setEditing((prev) => ({
      ...prev,
      roomCategories: (prev.roomCategories || []).map((category) =>
        category.id === categoryId ? { ...category, images: normalizedImages } : category,
      ),
    }));
    setRows((prev) =>
      prev.map((hotel) => ({
        ...hotel,
        roomCategories: (hotel.roomCategories || []).map((category) =>
          category.id === categoryId ? { ...category, images: normalizedImages } : category,
        ),
      })),
    );
  };

  const uploadCategoryImages = async (category, files) => {
    const selected = Array.from(files || []);
    if (!selected.length) return;
    if (!category.id) {
      toast.info("Save the hotel before uploading room media");
      return;
    }

    selected
      .filter((file) => file.size > MAX_ROOM_MEDIA_BYTES)
      .forEach((file) => {
        toast.error(
          `${file.name} is ${formatFileSize(file.size)} - the limit is ${formatFileSize(MAX_ROOM_MEDIA_BYTES)}`,
        );
      });
    const uploadable = selected.filter((file) => file.size <= MAX_ROOM_MEDIA_BYTES);
    if (!uploadable.length) return;

    // One request per file. A batch shares the 250MB request cap, so several large
    // videos together would fail as a whole and lose the files that were fine.
    let uploaded = 0;
    let failed = 0;
    for (let index = 0; index < uploadable.length; index += 1) {
      const file = uploadable[index];
      setCategoryImageUploading((prev) => ({
        ...prev,
        [category.id]: `Uploading ${index + 1} of ${uploadable.length}...`,
      }));
      try {
        const images = await uploadHotelRoomCategoryImages(category.id, [file]);
        setCategoryImages(category.id, images);
        uploaded += 1;
      } catch (error) {
        failed += 1;
        toast.error(
          error?.response?.data?.message || `Could not upload ${file.name}`,
        );
      }
    }
    setCategoryImageUploading((prev) => ({ ...prev, [category.id]: "" }));

    if (uploaded && !failed) {
      toast.success(
        `${uploaded} file${uploaded === 1 ? "" : "s"} uploaded`,
      );
    } else if (uploaded) {
      toast.success(`${uploaded} of ${uploadable.length} uploaded`);
    }
  };

  const deleteCategoryImage = async (category, image) => {
    if (!image?.id || !window.confirm("Remove this room image?")) return;
    try {
      await deleteHotelRoomCategoryImage(image.id);
      setCategoryImages(
        category.id,
        (category.images || []).filter((item) => item.id !== image.id),
      );
      toast.success("Room image removed");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not remove room image");
    }
  };

  const edit = (hotel) => {
    setEditing(normalizeHotel(hotel));
  };

  const reset = () => {
    setEditing({ ...blankHotel, roomCategories: [] });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = normalizeHotel(editing);
      const { data } = payload.id
        ? await api.put(`/api/admin/hotels/${payload.id}`, payload)
        : await api.post("/api/admin/hotels", payload);
      const saved = normalizeHotel(data);
      setRows((prev) =>
        payload.id ? prev.map((row) => (row.id === saved.id ? saved : row)) : [...prev, saved],
      );
      setEditing(saved);
      toast.success("Hotel saved");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not save hotel");
    } finally {
      setSaving(false);
    }
  };

  const uploadScanner = async (file) => {
    if (!file) return;
    setScannerUploading(true);
    try {
      patch("upiScannerUrl", await uploadHotelPaymentScanner(file));
      toast.success("Hotel UPI scanner uploaded");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not upload hotel UPI scanner");
    } finally {
      setScannerUploading(false);
    }
  };

  const remove = async (hotel) => {
    if (!hotel.id || !window.confirm(`Delete ${hotel.hotelName}?`)) return;
    try {
      await api.delete(`/api/admin/hotels/${hotel.id}`);
      setRows((prev) => prev.filter((row) => row.id !== hotel.id));
      if (editing.id === hotel.id) reset();
      toast.success("Hotel deleted");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not delete hotel");
    }
  };

  const copyRateCard = async (hotel) => {
    const text = buildRateCardText(hotel);
    if (!text) {
      toast.info(`No active room categories to send for ${hotel.hotelName || hotel.hotelCode}`);
      return;
    }
    try {
      await copyRateCardToClipboard(text);
      toast.success(`Rate card copied for ${hotel.hotelName || hotel.hotelCode}`);
    } catch {
      toast.error("Could not copy the rate card");
    }
  };

  const cityGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filteredRows = rows.filter((hotel) => {
      if (!query) return true;
      return [
        hotel.hotelName,
        hotel.hotelCode,
        hotel.city,
        hotel.caretakerName,
        hotel.caretakerNumber,
        hotel.ownerName,
        hotel.ownerNumber,
        ...(hotel.roomCategories || []).map((category) => category.categoryName),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query);
    });

    const groups = new Map();
    filteredRows.forEach((hotel) => {
      const city = hotel.city?.trim() || "City not set";
      if (!groups.has(city)) groups.set(city, []);
      groups.get(city).push(hotel);
    });

    return Array.from(groups.entries())
      .map(([city, hotels]) => ({
        city,
        hotels: hotels.sort(
          (a, b) =>
            Number(a.displayOrder ?? 0) - Number(b.displayOrder ?? 0) ||
            String(a.hotelName || "").localeCompare(String(b.hotelName || "")),
        ),
      }))
      .sort((a, b) => a.city.localeCompare(b.city));
  }, [rows, search]);

  return (
    <Card className="shadow-sm">
      <Card.Header>
        <div className="fw-semibold">Hotels Master</div>
        <div className="text-muted small">
          Create reusable hotels and room categories, then attach them in each trip's Dynamic Pricing rooms.
        </div>
      </Card.Header>
      <Card.Body>
        <Card className="mb-3">
          <Card.Body>
            <div className="fw-semibold mb-1">Facility Maintenance</div>
            <div className="small text-muted mb-3">Common facility master used by all hotels. Disabled facilities are hidden from customer quote output.</div>
            <div className="row g-2 align-items-end mb-3">
              <div className="col-md-5">
                <Form.Label className="small text-muted mb-1">New Facility</Form.Label>
                <Form.Control value={facilityDraft.name} onChange={(e) => setFacilityDraft((prev) => ({ ...prev, name: e.target.value }))} placeholder="e.g. Breakfast" />
              </div>
              <div className="col-md-2">
                <Form.Label className="small text-muted mb-1">Order</Form.Label>
                <Form.Control type="number" value={facilityDraft.displayOrder ?? 0} onChange={(e) => setFacilityDraft((prev) => ({ ...prev, displayOrder: Number(e.target.value || 0) }))} />
              </div>
              <div className="col-md-2">
                <Form.Check type="switch" label="Active" checked={facilityDraft.active !== false} onChange={(e) => setFacilityDraft((prev) => ({ ...prev, active: e.target.checked }))} />
              </div>
              <div className="col-md-3">
                <Button size="sm" variant="outline-primary" onClick={() => saveFacility()}>
                  Add Facility
                </Button>
              </div>
            </div>            <div className="d-flex flex-column gap-2">
              {facilities.map((facility) => (
                <div key={facility.id} className="d-flex flex-wrap align-items-center gap-2 border rounded px-2 py-2">
                  <Form.Control
                    size="sm"
                    value={facility.name || ""}
                    onChange={(e) => setFacilities((prev) => prev.map((item) => item.id === facility.id ? { ...item, name: e.target.value } : item))}
                    style={{ maxWidth: 260 }}
                  />
                  <Form.Control
                    size="sm"
                    type="number"
                    value={facility.displayOrder ?? 0}
                    onChange={(e) => setFacilities((prev) => prev.map((item) => item.id === facility.id ? { ...item, displayOrder: Number(e.target.value || 0) } : item))}
                    style={{ maxWidth: 90 }}
                  />
                  <Form.Check
                    type="switch"
                    label="Active"
                    checked={facility.active !== false}
                    onChange={(e) => setFacilities((prev) => prev.map((item) => item.id === facility.id ? { ...item, active: e.target.checked } : item))}
                  />
                  <Button size="sm" variant="outline-secondary" onClick={() => saveFacility(facility)}>Save</Button>
                  <Button size="sm" variant="outline-danger" onClick={() => deleteFacility(facility)}>Delete</Button>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>

        <Card className="mb-3">
          <Card.Body>
            <div className="fw-semibold mb-1">Stay Types</div>
            <div className="small text-muted mb-3">
              Customers see these as headings in the trip page's hotel list, with the description behind an info icon - so describe the kind of place, never name it. Disabled types can no longer be picked; hotels already using one keep it. Renaming a type renames it on every hotel.
            </div>
            <div className="row g-2 align-items-end mb-3">
              <div className="col-md-3">
                <Form.Label className="small text-muted mb-1">New Stay Type</Form.Label>
                <Form.Control
                  value={stayTypeDraft.name}
                  maxLength={STAY_TYPE_NAME_MAX}
                  onChange={(e) => setStayTypeDraft((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Homestay"
                />
              </div>
              <div className="col-md-4">
                <Form.Label className="small text-muted mb-1">Description (info icon)</Form.Label>
                <Form.Control
                  value={stayTypeDraft.description}
                  maxLength={STAY_TYPE_DESCRIPTION_MAX}
                  onChange={(e) => setStayTypeDraft((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="e.g. A room in a local family's home"
                />
              </div>
              <div className="col-md-1">
                <Form.Label className="small text-muted mb-1">Order</Form.Label>
                <Form.Control type="number" value={stayTypeDraft.displayOrder ?? 0} onChange={(e) => setStayTypeDraft((prev) => ({ ...prev, displayOrder: Number(e.target.value || 0) }))} />
              </div>
              <div className="col-md-2">
                <Form.Check type="switch" label="Active" checked={stayTypeDraft.active !== false} onChange={(e) => setStayTypeDraft((prev) => ({ ...prev, active: e.target.checked }))} />
              </div>
              <div className="col-md-2">
                <Button size="sm" variant="outline-primary" onClick={() => saveStayType()}>
                  Add Stay Type
                </Button>
              </div>
            </div>
            <div className="d-flex flex-column gap-2">
              {stayTypes.map((stayType) => {
                const used = hotelsUsingStayType(stayType.name);
                return (
                  <div key={stayType.id} className="d-flex flex-wrap align-items-center gap-2 border rounded px-2 py-2">
                    <Form.Control
                      size="sm"
                      value={stayType.name || ""}
                      maxLength={STAY_TYPE_NAME_MAX}
                      onChange={(e) => setStayTypes((prev) => prev.map((item) => item.id === stayType.id ? { ...item, name: e.target.value } : item))}
                      style={{ maxWidth: 220 }}
                    />
                    <Form.Control
                      size="sm"
                      value={stayType.description || ""}
                      maxLength={STAY_TYPE_DESCRIPTION_MAX}
                      placeholder="Description for the info icon"
                      onChange={(e) => setStayTypes((prev) => prev.map((item) => item.id === stayType.id ? { ...item, description: e.target.value } : item))}
                      style={{ flex: "1 1 260px", width: "auto" }}
                    />
                    <Form.Control
                      size="sm"
                      type="number"
                      value={stayType.displayOrder ?? 0}
                      onChange={(e) => setStayTypes((prev) => prev.map((item) => item.id === stayType.id ? { ...item, displayOrder: Number(e.target.value || 0) } : item))}
                      style={{ maxWidth: 90 }}
                    />
                    <Form.Check
                      type="switch"
                      label="Active"
                      checked={stayType.active !== false}
                      onChange={(e) => setStayTypes((prev) => prev.map((item) => item.id === stayType.id ? { ...item, active: e.target.checked } : item))}
                    />
                    <Button size="sm" variant="outline-secondary" onClick={() => saveStayType(stayType)}>Save</Button>
                    <Button size="sm" variant="outline-danger" onClick={() => deleteStayType(stayType)}>Delete</Button>
                    <span className="small text-muted">{used} hotel{used === 1 ? "" : "s"}</span>
                  </div>
                );
              })}
            </div>
          </Card.Body>
        </Card>

        <Card className="mb-3 shadow-sm">
          <Card.Body>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3 pb-3 border-bottom">
              <div>
                <div className="fw-semibold fs-5">{editing.id ? "Edit Hotel" : "New Hotel"}</div>
                <div className="text-muted small">
                  {editing.hotelName ? `${editing.hotelName}${editing.city ? ` - ${editing.city}` : ""}` : "Enter hotel details, facilities, payment, and room categories."}
                </div>
              </div>
              <div className="d-flex gap-2">
                <Button size="sm" variant="outline-secondary" onClick={reset}>
                  New Hotel
                </Button>
                <Button size="sm" onClick={save} disabled={saving}>
                  {saving ? "Saving..." : "Save Hotel"}
                </Button>
              </div>
            </div>

            <Accordion defaultActiveKey="basic" className="hotel-edit-accordion">
              <Accordion.Item eventKey="basic">
                <Accordion.Header>
                  <div className="d-flex flex-column">
                    <span className="fw-semibold">Basic Details</span>
                    <span className="small text-muted">Name, code, location, caretaker and status</span>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <div className="row g-3">
                    <div className="col-lg-4 col-md-6">
                      <Form.Label>Hotel Name</Form.Label>
                      <Form.Control value={editing.hotelName || ""} onChange={(e) => patch("hotelName", e.target.value)} />
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <Form.Label>Customer Code</Form.Label>
                      <Form.Control value={editing.hotelCode || ""} onChange={(e) => patch("hotelCode", e.target.value)} />
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <Form.Label>City</Form.Label>
                      {addingCity ? (
                        <InputGroup>
                          <Form.Control
                            autoFocus
                            placeholder="New city name"
                            value={editing.city || ""}
                            onChange={(e) => patch("city", e.target.value)}
                            onBlur={() => patch("city", String(editing.city || "").trim())}
                          />
                          <Button
                            variant="outline-secondary"
                            onClick={() => {
                              setAddingCity(false);
                              patch("city", "");
                            }}
                          >
                            Cancel
                          </Button>
                        </InputGroup>
                      ) : (
                        <Form.Select
                          value={editing.city || ""}
                          onChange={(e) => {
                            if (e.target.value === ADD_NEW_CITY) {
                              setAddingCity(true);
                              patch("city", "");
                              return;
                            }
                            patch("city", e.target.value);
                          }}
                        >
                          <option value="">Select city</option>
                          {knownCities.map((city) => <option key={city} value={city}>{city}</option>)}
                          <option value={ADD_NEW_CITY}>+ Add new city...</option>
                        </Form.Select>
                      )}
                    </div>
                    <div className="col-lg-2 col-md-6">
                      <Form.Label>Order</Form.Label>
                      <Form.Control type="number" value={editing.displayOrder ?? 0} onChange={(e) => patch("displayOrder", Number(e.target.value || 0))} />
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <Form.Label>Stay Type</Form.Label>
                      <Form.Select value={editing.stayType || ""} onChange={(e) => patch("stayType", e.target.value)}>
                        <option value="">Not set</option>
                        {stayTypeOptions(stayTypes, editing.stayType).map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">Shown to customers on the trip page.</Form.Text>
                    </div>
                    <div className="col-lg-3 col-md-6 d-flex align-items-center">
                      <Form.Check
                        type="switch"
                        id="hotel-iotrips-pick"
                        label="Iotrips pick (recommended to customers)"
                        checked={Boolean(editing.iotripsPick)}
                        onChange={(e) => patch("iotripsPick", e.target.checked)}
                      />
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <Form.Check
                        type="switch"
                        id="hotel-min-guests-per-room"
                        label="Minimum guests per room"
                        checked={Boolean(editing.minGuestsPerRoom)}
                        onChange={(e) => patch("minGuestsPerRoom", e.target.checked)}
                      />
                      <Form.Text className="text-muted">
                        For hotels that charge by head: a room is never given to fewer people than the smallest group it is priced for.
                      </Form.Text>
                    </div>
                    <div className="col-lg-6">
                      <Form.Label>Google Map Link</Form.Label>
                      <Form.Control
                        value={editing.googleMapLink || ""}
                        onChange={(e) => patch("googleMapLink", e.target.value)}
                        placeholder="Paste hotel Google Maps URL"
                      />
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <Form.Label>Caretaker Name</Form.Label>
                      <Form.Control
                        value={editing.caretakerName || ""}
                        onChange={(e) => patch("caretakerName", e.target.value)}
                        placeholder="Caretaker name"
                      />
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <Form.Label>Caretaker Number</Form.Label>
                      <Form.Control
                        value={editing.caretakerNumber || ""}
                        onChange={(e) => patch("caretakerNumber", e.target.value)}
                        placeholder="Caretaker phone number"
                      />
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <Form.Label>Owner Name</Form.Label>
                      <Form.Control
                        value={editing.ownerName || ""}
                        onChange={(e) => patch("ownerName", e.target.value)}
                        placeholder="Owner name"
                      />
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <Form.Label>Owner Number</Form.Label>
                      <Form.Control
                        value={editing.ownerNumber || ""}
                        onChange={(e) => patch("ownerNumber", e.target.value)}
                        placeholder="Owner phone number"
                      />
                    </div>
                    <div className="col-12">
                      <Form.Check type="switch" label="Hotel is active" checked={editing.active !== false} onChange={(e) => patch("active", e.target.checked)} />
                    </div>
                  </div>
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="facilities">
                <Accordion.Header>
                  <div className="d-flex flex-column">
                    <span className="fw-semibold">Hotel Facilities</span>
                    <span className="small text-muted">{(editing.facilities || []).length} selected</span>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <div className="small text-muted mb-3">Use Included externally when IO Trips must arrange it but the customer should see it as included.</div>
                  <div className="d-flex flex-column gap-2">
                    {facilities.map((facility) => {
                      const selectedFacility = (editing.facilities || []).find((item) => item.id === facility.id);
                      const checked = Boolean(selectedFacility);
                      return (
                        <div key={facility.id} className={`border rounded px-3 py-3 ${checked ? "bg-light" : ""}`}>
                          <div className="row g-2 align-items-center">
                            <div className="col-lg-3 col-md-6">
                              <Form.Check
                                type="checkbox"
                                id={`hotel-facility-${facility.id}`}
                                label={`${facility.name}${facility.active === false ? " (disabled)" : ""}`}
                                checked={checked}
                                onChange={() => toggleHotelFacility(facility)}
                              />
                            </div>
                            {checked && (
                              <>
                                <div className="col-lg-3 col-md-6">
                                  <Form.Label className="small text-muted mb-1">Cost behavior</Form.Label>
                                  <Form.Select
                                    size="sm"
                                    value={selectedFacility.costBehavior || (selectedFacility.optionalWithCost ? "OPTIONAL_PAID" : "INCLUDED_BY_HOTEL")}
                                    onChange={(e) => patchHotelFacility(facility.id, "costBehavior", e.target.value)}
                                  >
                                    <option value="INCLUDED_BY_HOTEL">Included by hotel</option>
                                    <option value="INCLUDED_EXTERNAL">Included externally</option>
                                    <option value="OPTIONAL_PAID">Optional paid</option>
                                  </Form.Select>
                                </div>
                                <div className="col-lg-2 col-md-6">
                                  <Form.Label className="small text-muted mb-1">Pricing type</Form.Label>
                                  <Form.Select
                                    size="sm"
                                    value={selectedFacility.pricingType || "PER_ACTIVITY"}
                                    onChange={(e) => patchHotelFacility(facility.id, "pricingType", e.target.value)}
                                    disabled={!facilityHasCost(selectedFacility)}
                                  >
                                    <option value="PER_ACTIVITY">Per activity</option>
                                    <option value="PER_PERSON">Per person</option>
                                  </Form.Select>
                                </div>
                                {facilityCostBehavior(selectedFacility) === "OPTIONAL_PAID" ? (
                                  <>
                                    <div className="col-lg-2 col-md-6">
                                      <Form.Label className="small text-muted mb-1">Hotel payable</Form.Label>
                                      <Money
                                        value={selectedFacility.hotelPayable || 0}
                                        onChange={(value) => patchHotelFacility(facility.id, "hotelPayable", value)}
                                      />
                                    </div>
                                    <div className="col-lg-2 col-md-6">
                                      <Form.Label className="small text-muted mb-1">Customer price</Form.Label>
                                      <CustomerPrice
                                        value={selectedFacility.extraCost}
                                        hotelPayable={selectedFacility.hotelPayable}
                                        onChange={(value) => patchHotelFacility(facility.id, "extraCost", value)}
                                      />
                                    </div>
                                  </>
                                ) : (
                                  <div className="col-lg-2 col-md-6">
                                    <Form.Label className="small text-muted mb-1">Amount</Form.Label>
                                    <Money
                                      value={selectedFacility.extraCost || 0}
                                      onChange={(value) => patchHotelFacility(facility.id, "extraCost", value)}
                                      disabled={!facilityHasCost(selectedFacility)}
                                    />
                                  </div>
                                )}
                                {facilityCostBehavior(selectedFacility) === "INCLUDED_EXTERNAL" && (
                                  <div className="col-lg-3 col-md-6">
                                    <Form.Label className="small text-muted mb-1">Default external vendor</Form.Label>
                                    <Form.Select
                                      size="sm"
                                      value={selectedFacility.externalVendorId || ""}
                                      onChange={(e) => patchHotelFacility(facility.id, "externalVendorId", e.target.value ? Number(e.target.value) : "")}
                                    >
                                      <option value="">Select vendor</option>
                                      {vendorsForHotelCity(externalVendors, editing.city).map((vendor) => (
                                        <option key={vendor.id} value={vendor.id}>
                                          {vendor.name} - {vendor.city}
                                        </option>
                                      ))}
                                    </Form.Select>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="payment">
                <Accordion.Header>
                  <div className="d-flex flex-column">
                    <span className="fw-semibold">Hotel Payment Details</span>
                    <span className="small text-muted">{editing.upiScannerUrl ? "UPI scanner uploaded" : "UPI scanner not uploaded"}</span>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <div className="small text-muted mb-3">Used for Hotel Vendor Payments in Booking Edit. Provide UPI, scanner URL, or bank account details.</div>
                  <div className="row g-3">
                    <div className="col-lg-4 col-md-6">
                      <Form.Label>UPI Number / ID</Form.Label>
                      <Form.Control value={editing.upiNumber || ""} onChange={(e) => patch("upiNumber", e.target.value)} />
                    </div>
                    <div className="col-lg-4 col-md-6">
                      <Form.Label>Upload UPI Scanner</Form.Label>
                      <Form.Control type="file" accept="image/*" disabled={scannerUploading} onChange={(e) => uploadScanner(e.target.files?.[0])} />
                      {scannerUploading && <div className="small text-muted">Uploading scanner...</div>}
                      {editing.upiScannerUrl && <a href={editing.upiScannerUrl} target="_blank" rel="noreferrer" className="small">View uploaded scanner</a>}
                    </div>
                    <div className="col-lg-4 col-md-12">
                      <Form.Label>UPI Scanner URL</Form.Label>
                      <Form.Control value={editing.upiScannerUrl || ""} readOnly placeholder="Uploaded scanner URL" />
                    </div>
                    <div className="col-lg-4 col-md-6">
                      <Form.Label>Account Holder</Form.Label>
                      <Form.Control value={editing.bankAccountHolder || ""} onChange={(e) => patch("bankAccountHolder", e.target.value)} />
                    </div>
                    <div className="col-lg-4 col-md-6">
                      <Form.Label>Account Number</Form.Label>
                      <Form.Control value={editing.bankAccountNumber || ""} onChange={(e) => patch("bankAccountNumber", e.target.value)} />
                    </div>
                    <div className="col-lg-4 col-md-6">
                      <Form.Label>IFSC</Form.Label>
                      <Form.Control value={editing.bankIfsc || ""} onChange={(e) => patch("bankIfsc", e.target.value)} />
                    </div>
                    <div className="col-lg-4 col-md-6">
                      <Form.Label>Bank Name</Form.Label>
                      <Form.Control value={editing.bankName || ""} onChange={(e) => patch("bankName", e.target.value)} />
                    </div>
                    <div className="col-lg-4 col-md-6">
                      <Form.Label>Branch</Form.Label>
                      <Form.Control value={editing.bankBranch || ""} onChange={(e) => patch("bankBranch", e.target.value)} />
                    </div>
                  </div>
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="rooms">
                <Accordion.Header>
                  <div className="d-flex flex-column">
                    <span className="fw-semibold">Room Categories</span>
                    <span className="small text-muted">{(editing.roomCategories || []).length} categories configured</span>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <div className="d-flex justify-content-end mb-3">
                    <Button size="sm" variant="outline-primary" onClick={addCategory}>
                      Add Category
                    </Button>
                  </div>
                  <div className="d-flex flex-column gap-3">
              {(editing.roomCategories || []).map((category, index) => (
                <div className="border rounded p-3" key={index}>
                  <div className="row g-2 align-items-end">
                    <div className="col-lg-3 col-md-6">
                      <Form.Label className="small text-muted mb-1">Category</Form.Label>
                      <Form.Control value={category.categoryName || ""} onChange={(e) => patchCategory(index, "categoryName", e.target.value)} />
                    </div>
                    <div className="col-lg-2 col-md-6">
                      <Form.Label className="small text-muted mb-1">Pricing</Form.Label>
                      <Form.Select
                        value={category.pricingType || "PER_ROOM"}
                        onChange={(e) => patchCategory(index, "pricingType", e.target.value)}
                      >
                        <option value="PER_ROOM">Per room</option>
                        <option value="PER_PERSON">Per person</option>
                      </Form.Select>
                    </div>
                    <div className="col-lg-2 col-md-4">
                      <Form.Label className="small text-muted mb-1">Category max units (inclusive)</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          min="0"
                          step="1"
                          value={activeRoomCount(category) || (category.maximumUnits ?? 0)}
                          disabled={activeRoomCount(category) > 0}
                          onChange={(e) => patchCategory(index, "maximumUnits", Number(e.target.value || 0))}
                        />
                        <InputGroup.Text>units</InputGroup.Text>
                      </InputGroup>
                      {activeRoomCount(category) > 0 && (
                        <div className="small text-muted mt-1">Set by the room numbers below.</div>
                      )}
                    </div>
                    <div className="col-lg-2 col-md-4">
                      <Form.Label className="small text-muted mb-1">Order</Form.Label>
                      <Form.Control type="number" min="0" step="1" value={category.displayOrder ?? 0} onChange={(e) => patchCategory(index, "displayOrder", Number(e.target.value || 0))} />
                    </div>
                    <div className="col-lg-1 col-md-4">
                      <Form.Label className="small text-muted mb-1 d-block">Active</Form.Label>
                      <Form.Check checked={category.active !== false} onChange={(e) => patchCategory(index, "active", e.target.checked)} />
                    </div>
                    <div className="col-lg-2 text-lg-end">
                      <Button size="sm" variant="outline-danger" onClick={() => removeCategory(index)}>
                        Remove Category
                      </Button>
                    </div>
                  </div>

                  <div className="row g-2 align-items-end mt-2">
                    <div className="col-lg-2 col-md-6">
                      <Form.Label className="small text-muted mb-1">Extra mattress - hotel payable</Form.Label>
                      <Money value={category.extraMattressPayable} onChange={(value) => patchCategory(index, "extraMattressPayable", value)} />
                    </div>
                    <div className="col-lg-2 col-md-6">
                      <Form.Label className="small text-muted mb-1">Extra mattress - customer price</Form.Label>
                      <CustomerPrice
                        value={category.extraMattressPrice}
                        hotelPayable={category.extraMattressPayable}
                        onChange={(value) => patchCategory(index, "extraMattressPrice", value)}
                      />
                    </div>
                    <div className="col-lg-2 col-md-6">
                      <Form.Label className="small text-muted mb-1">Kid age threshold (this age and under not counted)</Form.Label>
                      <InputGroup>
                        <Form.Control type="number" min="0" step="1" value={category.kidRoomCountAgeThreshold ?? 3} onChange={(e) => patchCategory(index, "kidRoomCountAgeThreshold", Number(e.target.value || 0))} />
                        <InputGroup.Text>yrs</InputGroup.Text>
                      </InputGroup>
                    </div>
                    <div className="col-lg-2 col-md-6">
                      <Form.Label className="small text-muted mb-1">Kids grace / room (up to, inclusive)</Form.Label>
                      <InputGroup>
                        <Form.Control type="number" min="0" step="1" value={category.kidGracePerRoom ?? 2} onChange={(e) => patchCategory(index, "kidGracePerRoom", Number(e.target.value || 0))} />
                        <InputGroup.Text>kids</InputGroup.Text>
                      </InputGroup>
                    </div>
                    {category.pricingType === "PER_PERSON" && (
                      <div className="col-lg-3 col-md-6">
                        <Form.Label className="small text-muted mb-1">Per person price</Form.Label>
                        <Money value={category.perPersonPrice} onChange={(value) => patchCategory(index, "perPersonPrice", value)} />
                      </div>
                    )}
                  </div>

                  {category.pricingType !== "PER_PERSON" && (
                    <div className="mt-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="small fw-semibold">
                          Member Count Prices
                          {editing.minGuestsPerRoom && minGuestsFor(category) > 0 && (
                            <span className="fw-normal text-muted ms-2">
                              Minimum {minGuestsFor(category)} guests per room - the smallest group priced
                            </span>
                          )}
                        </div>
                        <Button size="sm" variant="outline-secondary" onClick={() => addPriceOption(index)}>
                          Add Price
                        </Button>
                      </div>
                      <div className="d-flex flex-column gap-2">
                        {(category.priceOptions || []).map((option, optionIndex) => (
                          <div className="row g-2 align-items-end" key={optionIndex}>
                            <div className="col-lg-2 col-md-4">
                              <Form.Label className="small text-muted mb-1">Members</Form.Label>
                              <InputGroup>
                                <Form.Control
                                  type="number"
                                  min="1"
                                  step="1"
                                  value={option.memberCount ?? 2}
                                  onChange={(e) => patchPriceOption(index, optionIndex, "memberCount", Number(e.target.value || 1))}
                                />
                                <InputGroup.Text>members</InputGroup.Text>
                              </InputGroup>
                            </div>
                            <div className="col-lg-2 col-md-4">
                              <Form.Label className="small text-muted mb-1">Hotel payable</Form.Label>
                              <Money value={option.hotelPayable} onChange={(value) => patchPriceOption(index, optionIndex, "hotelPayable", value)} />
                            </div>
                            <div className="col-lg-2 col-md-4">
                              <Form.Label className="small text-muted mb-1">Customer price</Form.Label>
                              <CustomerPrice
                                value={option.cost}
                                hotelPayable={option.hotelPayable}
                                onChange={(value) => patchPriceOption(index, optionIndex, "cost", value)}
                              />
                            </div>
                            <div className="col-lg-2 col-md-4">
                              <Form.Label className="small text-muted mb-1">Margin</Form.Label>
                              <MarginText payable={option.hotelPayable} customerPrice={option.cost} />
                            </div>
                            <div className="col-lg-1 col-md-4">
                              <Form.Label className="small text-muted mb-1">Order</Form.Label>
                              <Form.Control type="number" min="0" step="1" value={option.displayOrder ?? 0} onChange={(e) => patchPriceOption(index, optionIndex, "displayOrder", Number(e.target.value || 0))} />
                            </div>
                            <div className="col-lg-2 col-md-4">
                              <Button size="sm" variant="outline-danger" onClick={() => removePriceOption(index, optionIndex)}>
                                Remove Price
                              </Button>
                            </div>
                          </div>
                        ))}
                        {!category.priceOptions?.length && (
                          <div className="text-muted small py-2">No member count prices configured.</div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="mt-3 border-top pt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <div className="small fw-semibold">Room Numbers</div>
                        <div className="small text-muted">
                          Optional. Add the hotel's own room numbers in the order they should be handed out -
                          the first free room in this list is assigned first. Leave empty for hotels that do not
                          give us room numbers. Never shown to customers.
                        </div>
                      </div>
                      <Button size="sm" variant="outline-secondary" onClick={() => addRoomUnit(index)}>
                        Add Room
                      </Button>
                    </div>
                    <div className="d-flex flex-column gap-2">
                      {(category.rooms || []).map((room, roomIndex) => (
                        <div className="row g-2 align-items-end" key={roomIndex}>
                          <div className="col-lg-1 col-md-2">
                            <Form.Label className="small text-muted mb-1">Rank</Form.Label>
                            <Form.Control plaintext readOnly value={roomIndex + 1} className="px-2" />
                          </div>
                          <div className="col-lg-2 col-md-4">
                            <Form.Label className="small text-muted mb-1">Room number</Form.Label>
                            <Form.Control
                              value={room.roomNumber || ""}
                              placeholder="103"
                              onChange={(e) => patchRoomUnit(index, roomIndex, "roomNumber", e.target.value)}
                            />
                          </div>
                          <div className="col-lg-4 col-md-6">
                            <Form.Label className="small text-muted mb-1">Note (internal)</Form.Label>
                            <Form.Control
                              value={room.note || ""}
                              placeholder="2-side balcony"
                              onChange={(e) => patchRoomUnit(index, roomIndex, "note", e.target.value)}
                            />
                          </div>
                          <div className="col-lg-1 col-md-2">
                            <Form.Label className="small text-muted mb-1 d-block">Active</Form.Label>
                            <Form.Check
                              checked={room.active !== false}
                              onChange={(e) => patchRoomUnit(index, roomIndex, "active", e.target.checked)}
                            />
                          </div>
                          <div className="col-lg-4 col-md-6 d-flex gap-2">
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              disabled={roomIndex === 0}
                              onClick={() => moveRoomUnit(index, roomIndex, -1)}
                            >
                              Up
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              disabled={roomIndex === (category.rooms || []).length - 1}
                              onClick={() => moveRoomUnit(index, roomIndex, 1)}
                            >
                              Down
                            </Button>
                            <Button size="sm" variant="outline-danger" onClick={() => removeRoomUnit(index, roomIndex)}>
                              Remove
                            </Button>
                          </div>
                        </div>
                      ))}
                      {!category.rooms?.length && (
                        <div className="text-muted small py-2">
                          No room numbers configured - this category is tracked by count only.
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 border-top pt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <div className="small fw-semibold">Special Date Pricing</div>
                        <div className="small text-muted">Use for long weekends or specific date ranges. Leave members blank for per-person categories.</div>
                      </div>
                      <Button size="sm" variant="outline-secondary" onClick={() => addSpecialPrice(index)}>
                        Add Special Price
                      </Button>
                    </div>
                    <div className="d-flex flex-column gap-3">
                      {specialPriceGroups(category.specialPrices || []).map((group, groupIndex) => (
                        <div className="border rounded p-3 bg-light" key={group.key}>
                          <div className="row g-2 align-items-end">
                            <div className="col-lg-2 col-md-4">
                              <Form.Label className="small text-muted mb-1">Rule</Form.Label>
                              <Form.Select
                                value={group.rule.recurrenceType || "DATE_RANGE"}
                                onChange={(e) => patchSpecialPriceGroup(index, group, "recurrenceType", e.target.value)}
                              >
                                <option value="DATE_RANGE">Custom dates</option>
                                <option value="WEEKLY">Weekly days</option>
                              </Form.Select>
                            </div>
                            {(group.rule.recurrenceType || "DATE_RANGE") === "WEEKLY" ? (
                              <div className="col-lg-4 col-md-8">
                                <Form.Label className="small text-muted mb-1">Days</Form.Label>
                                <div className="d-flex flex-wrap gap-2">
                                  {weekDays.map((day) => (
                                    <Form.Check
                                      key={day.bit}
                                      type="checkbox"
                                      id={`hotel-special-group-day-${index}-${groupIndex}-${day.bit}`}
                                      label={day.label}
                                      checked={(Number(group.rule.dayOfWeekMask || 0) & day.bit) !== 0}
                                      onChange={() => toggleSpecialPriceGroupWeekday(index, group, day.bit)}
                                    />
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="col-lg-2 col-md-4">
                                  <Form.Label className="small text-muted mb-1">From</Form.Label>
                                  <Form.Control
                                    type="date"
                                    value={group.rule.fromDate || ""}
                                    onChange={(e) => patchSpecialPriceGroup(index, group, "fromDate", e.target.value)}
                                  />
                                </div>
                                <div className="col-lg-2 col-md-4">
                                  <Form.Label className="small text-muted mb-1">To</Form.Label>
                                  <Form.Control
                                    type="date"
                                    value={group.rule.toDate || ""}
                                    onChange={(e) => patchSpecialPriceGroup(index, group, "toDate", e.target.value)}
                                  />
                                </div>
                              </>
                            )}
                            <div className="col-lg-3 col-md-6">
                              <Form.Label className="small text-muted mb-1">Note</Form.Label>
                              <Form.Control
                                value={group.rule.note || ""}
                                placeholder="Long weekend"
                                onChange={(e) => patchSpecialPriceGroup(index, group, "note", e.target.value)}
                              />
                            </div>
                            <div className="col-lg-1 col-md-2">
                              <Form.Label className="small text-muted mb-1 d-block">Active</Form.Label>
                              <Form.Check
                                checked={group.rule.active !== false}
                                onChange={(e) => patchSpecialPriceGroup(index, group, "active", e.target.checked)}
                              />
                            </div>
                            <div className="col-lg-2 col-md-4 d-flex gap-2">
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => addSpecialMemberPrice(index, group, category.pricingType)}
                                disabled={category.pricingType === "PER_PERSON" && group.prices.length > 0}
                              >
                                Add Member Price
                              </Button>
                              <Button size="sm" variant="outline-danger" onClick={() => removeSpecialPriceGroup(index, group)}>
                                Remove Rule
                              </Button>
                            </div>
                          </div>

                          <div className="mt-3">
                            <div className="small fw-semibold mb-2">Member Prices</div>
                            <div className="d-flex flex-column gap-2">
                              {group.prices.map(({ rowIndex, row }) => (
                                <div className="row g-2 align-items-end" key={row.id || rowIndex}>
                                  <div className="col-lg-3 col-md-5">
                                    <Form.Label className="small text-muted mb-1">Members</Form.Label>
                                    <InputGroup>
                                      <Form.Control
                                        type="number"
                                        min="1"
                                        step="1"
                                        value={row.memberCount ?? ""}
                                        disabled={category.pricingType === "PER_PERSON"}
                                        placeholder={category.pricingType === "PER_PERSON" ? "Per person" : ""}
                                        onChange={(e) => patchSpecialMemberPrice(index, rowIndex, "memberCount", e.target.value ? Number(e.target.value) : "")}
                                      />
                                      <InputGroup.Text>members</InputGroup.Text>
                                    </InputGroup>
                                  </div>
                                  <div className="col-lg-2 col-md-4">
                                    <Form.Label className="small text-muted mb-1">Hotel payable</Form.Label>
                                    <Money value={row.hotelPayable} onChange={(value) => patchSpecialMemberPrice(index, rowIndex, "hotelPayable", value)} />
                                  </div>
                                  <div className="col-lg-2 col-md-4">
                                    <Form.Label className="small text-muted mb-1">Customer price</Form.Label>
                                    <CustomerPrice
                                      value={row.cost}
                                      hotelPayable={row.hotelPayable}
                                      onChange={(value) => patchSpecialMemberPrice(index, rowIndex, "cost", value)}
                                    />
                                  </div>
                                  <div className="col-lg-2 col-md-4">
                                    <Form.Label className="small text-muted mb-1">Margin</Form.Label>
                                    <MarginText payable={row.hotelPayable} customerPrice={row.cost} />
                                  </div>
                                  <div className="col-lg-2 col-md-2">
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      onClick={() => removeSpecialPrice(index, rowIndex)}
                                      disabled={group.prices.length <= 1}
                                    >
                                      Remove Price
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                      {!category.specialPrices?.length && (
                        <div className="text-muted small py-2">No special date prices configured.</div>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 border-top pt-3">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                      <div>
                        <div className="small fw-semibold">Room Images & Videos</div>
                        <div className="small text-muted">Visible to Chat and Call team by customer code only.</div>
                      </div>
                      <div style={{ minWidth: 260 }}>
                        <Form.Control
                          size="sm"
                          type="file"
                          accept="image/*,video/*"
                          multiple
                          disabled={!category.id || categoryImageUploading[category.id]}
                          onChange={(e) => {
                            uploadCategoryImages(category, e.target.files);
                            e.target.value = "";
                          }}
                        />
                        {!category.id && <div className="small text-muted mt-1">Save the hotel before uploading media.</div>}
                        <div className="small text-muted mt-1">Max {formatFileSize(MAX_ROOM_MEDIA_BYTES)} per file.</div>
                        {categoryImageUploading[category.id] && (
                          <div className="small text-muted mt-1">
                            {categoryImageUploading[category.id]}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {(category.images || []).map((image) => (
                        <div className="border rounded p-2" key={image.id || image.imageUrl} style={{ width: 150 }}>
                          {image.mediaType === "VIDEO" ? (
                            <video src={image.imageUrl} controls preload="metadata" className="rounded" style={{ height: 90, width: "100%", objectFit: "cover" }} />
                          ) : (
                            <a href={image.imageUrl} target="_blank" rel="noreferrer">
                              <img
                                src={image.imageUrl}
                                alt={category.categoryName || "Room"}
                                className="img-fluid rounded"
                                style={{ height: 90, width: "100%", objectFit: "cover" }}
                              />
                            </a>
                          )}
                          <Button
                            size="sm"
                            variant="outline-danger"
                            className="w-100 mt-2"
                            onClick={() => deleteCategoryImage(category, image)}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                      {!category.images?.length && (
                        <div className="small text-muted py-2">No room images or videos uploaded.</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {!editing.roomCategories?.length && (
                <div className="text-center text-muted border rounded py-4">
                  Add room categories such as Standard, Deluxe, or Suite.
                </div>
              )}
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

            <div className="d-flex justify-content-end mt-3 pt-3 border-top">
              <Button onClick={save} disabled={saving}>
                {saving ? "Saving..." : "Save Hotel"}
              </Button>
            </div>
          </Card.Body>
        </Card>

        {loading ? (
          <div className="text-center py-4">
            <Spinner />
          </div>
        ) : (
          <>
            <InputGroup className="mb-3">
              <InputGroup.Text>Search</InputGroup.Text>
              <Form.Control
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by city, hotel, customer code, or room category"
              />
            </InputGroup>

            {!cityGroups.length && (
              <div className="text-center text-muted py-4">No hotels found.</div>
            )}

            {cityGroups.map((group) => (
              <div key={group.city} className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="fw-semibold">{group.city}</div>
                  <div className="text-muted small">
                    {group.hotels.length} hotel{group.hotels.length === 1 ? "" : "s"}
                  </div>
                </div>
                <Accordion alwaysOpen>
                  {group.hotels.map((hotel) => (
                    <Accordion.Item eventKey={String(hotel.id)} key={hotel.id}>
                      <Accordion.Header>
                        {hotel.hotelName} ({hotel.hotelCode})
                      </Accordion.Header>
                      <Accordion.Body>
                        <div className="d-flex justify-content-end gap-2 mb-2">
                          <div className="small text-muted me-auto align-self-center">
                            <div>
                              Caretaker: {[hotel.caretakerName, hotel.caretakerNumber].filter(Boolean).join(" - ") || "Not set"}
                            </div>
                            <div>
                              Owner: {[hotel.ownerName, hotel.ownerNumber].filter(Boolean).join(" - ") || "Not set"}
                            </div>
                            <div>
                              UPI Scanner: {hotel.upiScannerUrl ? "Uploaded" : "Not uploaded"}
                            </div>
                            <div>
                              Stay Type: {hotel.stayType || "Not set"}
                            </div>
                            <div>
                              Facilities: {hotelFacilitySummary(hotel.facilities, facilities) || "None"}
                            </div>
                            {hotel.minGuestsPerRoom && (
                              <div>Minimum guests per room: On</div>
                            )}
                          </div>
                          {hotel.googleMapLink ? (
                            <Button as="a" href={hotel.googleMapLink} target="_blank" rel="noreferrer" size="sm" variant="outline-secondary">
                              Open Map
                            </Button>
                          ) : null}
                          <Button size="sm" variant="outline-success" onClick={() => copyRateCard(hotel)}>
                            <i className="bi bi-currency-rupee me-1" aria-hidden="true" />
                            Copy Rate Card
                          </Button>
                          {rateCardWhatsappNumber(hotel.caretakerNumber) && buildRateCardText(hotel) ? (
                            <Button
                              as="a"
                              size="sm"
                              variant="success"
                              href={`https://wa.me/${rateCardWhatsappNumber(hotel.caretakerNumber)}?text=${encodeURIComponent(buildRateCardText(hotel))}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <i className="bi bi-whatsapp me-1" aria-hidden="true" />
                              WhatsApp
                            </Button>
                          ) : null}
                          <Button size="sm" variant="outline-primary" onClick={() => edit(hotel)}>
                            Edit
                          </Button>
                          <Button size="sm" variant="outline-danger" onClick={() => remove(hotel)}>
                            Delete
                          </Button>
                        </div>
                        <Table responsive size="sm" className="align-middle mb-0">
                          <thead>
                            <tr><th style={{ width: 44 }}>#</th>
                              <th>Category</th>
                              <th>Pricing</th>
                              <th>Min / room (incl.)</th>
                              <th>Member Count Prices</th>
                              <th>Extra mattress</th>
                              <th>Kid age (and under, not counted)</th>
                              <th>Kids grace / room (incl.)</th>
                              <th>Max units (incl.)</th>
                              <th>Media</th>
                              <th>Active</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(hotel.roomCategories || []).map((category, index) => (
                              <tr key={category.id || category.categoryName}><td className="small fw-semibold">{index + 1}</td>
                                <td>{category.categoryName}</td>
                                <td>{category.pricingType === "PER_PERSON" ? "Per person" : "Per room"}</td>
                                <td>{hotel.minGuestsPerRoom && minGuestsFor(category) > 0 ? `${minGuestsFor(category)} guests` : "-"}</td>
                                <td>
                                  {category.pricingType === "PER_PERSON"
                                    ? `Rs. ${category.perPersonPrice || 0} / person`
                                    : (category.priceOptions || [])
                                        .map((option) => `${option.memberCount} member: ${priceLabel(option.hotelPayable, option.cost)}`)
                                        .join(", ") || "-"}
                                </td>
                                <td>{priceLabel(category.extraMattressPayable, category.extraMattressPrice)}</td>
                                <td>{category.kidRoomCountAgeThreshold} yrs</td>
                                <td>{category.kidGracePerRoom ?? 2}</td>
                                <td>{category.maximumUnits ?? 0}</td>
                                <td>{mediaSummary(category.images)}</td>
                                <td>{category.active === false ? "No" : "Yes"}</td>
                              </tr>
                            ))}
                            {!hotel.roomCategories?.length && (
                              <tr>
                                <td colSpan={11} className="text-center text-muted py-3">
                                  No room categories configured.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </div>
            ))}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

function Money({ value, onChange, disabled = false }) {
  return (
    <InputGroup size="sm">
      <InputGroup.Text>Rs.</InputGroup.Text>
      <Form.Control type="number" min="0" step="0.01" value={value ?? 0} disabled={disabled} onChange={(e) => onChange(Number(e.target.value || 0))} />
    </InputGroup>
  );
}

// Optional: left blank, quotations charge the hotel payable. It can never be below the payable -
// the backend refuses that on save, and the field is marked here before it gets that far.
function CustomerPrice({ value, hotelPayable, onChange, disabled = false }) {
  const blank = isBlankAmount(value);
  return (
    <InputGroup size="sm" hasValidation>
      <InputGroup.Text>Rs.</InputGroup.Text>
      <Form.Control
        type="number"
        min="0"
        step="0.01"
        value={blank ? "" : value}
        placeholder="Same as payable"
        disabled={disabled}
        isInvalid={marginOf(hotelPayable, value) < 0}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      />
      <Form.Control.Feedback type="invalid">Below the hotel payable</Form.Control.Feedback>
    </InputGroup>
  );
}

function MarginText({ payable, customerPrice }) {
  const below = marginOf(payable, customerPrice) < 0;
  return (
    <div className={`form-control-plaintext form-control-sm py-1 ${below ? "text-danger fw-semibold" : ""}`}>
      {marginLabel(payable, customerPrice)}
    </div>
  );
}

function facilityHasCost(facility) {
  const behavior = facilityCostBehavior(facility);
  return behavior === "INCLUDED_EXTERNAL" || behavior === "OPTIONAL_PAID";
}

function normalizeFacilityPatch(item, field, value) {
  if (field !== "costBehavior") return { ...item, [field]: value };
  const costBehavior = value || "INCLUDED_BY_HOTEL";
  return {
    ...item,
    costBehavior,
    optionalWithCost: costBehavior === "OPTIONAL_PAID",
  };
}

function facilityCostBehavior(facility) {
  if (facility?.costBehavior === "INCLUDED_EXTERNAL") return "INCLUDED_EXTERNAL";
  if (facility?.costBehavior === "OPTIONAL_PAID" || facility?.optionalWithCost === true) return "OPTIONAL_PAID";
  if (Number(facility?.extraCost || 0) > 0) return "INCLUDED_EXTERNAL";
  return "INCLUDED_BY_HOTEL";
}

// Active types, plus the hotel's current one even if it was disabled, so editing a hotel never
// silently shows a different type than the one customers see.
function stayTypeOptions(stayTypes, current) {
  const options = stayTypes
    .filter((type) => type.active !== false || type.name === current)
    .map((type) => ({ value: type.name, label: `${type.name}${type.active === false ? " (disabled)" : ""}` }));
  if (current && !options.some((option) => option.value === current)) {
    options.push({ value: current, label: `${current} (not in list)` });
  }
  return options;
}

// One line for the hotel list, in facility-master order. Prices are only worth showing for what
// the customer can add on.
function hotelFacilitySummary(selected = [], master = []) {
  const order = new Map(master.map((facility, index) => [facility.id, index]));
  return [...selected]
    .sort((a, b) => (order.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (order.get(b.id) ?? Number.MAX_SAFE_INTEGER))
    .map((facility) => {
      const behavior = facilityCostBehavior(facility);
      if (behavior === "OPTIONAL_PAID") {
        const per = facility.pricingType === "PER_PERSON" ? " / person" : "";
        return `${facility.name} (optional, ${priceLabel(facility.hotelPayable, facility.extraCost)}${per})`;
      }
      if (behavior === "INCLUDED_EXTERNAL") {
        return `${facility.name} (external${facility.externalVendorName ? `: ${facility.externalVendorName}` : ""})`;
      }
      return facility.name;
    })
    .join(", ");
}

function vendorsForHotelCity(vendors, city) {
  const hotelCity = String(city || "").trim().toLowerCase();
  const active = (Array.isArray(vendors) ? vendors : []).filter((vendor) => vendor.active !== false);
  if (!hotelCity) return active;
  const cityMatches = active.filter((vendor) => String(vendor.city || "").trim().toLowerCase() === hotelCity);
  return cityMatches.length ? cityMatches : active;
}

function mediaSummary(images = []) {
  const rows = Array.isArray(images) ? images : [];
  const imageCount = rows.filter((item) => item.mediaType !== "VIDEO").length;
  const videoCount = rows.filter((item) => item.mediaType === "VIDEO").length;
  return `${imageCount} image${imageCount === 1 ? "" : "s"}, ${videoCount} video${videoCount === 1 ? "" : "s"}`;
}

function normalizeHotel(row = {}) {
  return {
    ...row,
    hotelName: row.hotelName || "",
    hotelCode: row.hotelCode || "",
    city: row.city || "",
    googleMapLink: row.googleMapLink || "",
    caretakerNumber: row.caretakerNumber || row.caretaker_number || "",
    caretakerName: row.caretakerName || row.caretaker_name || "",
    ownerName: row.ownerName || row.owner_name || "",
    ownerNumber: row.ownerNumber || row.owner_number || "",
    facilities: Array.isArray(row.facilities) ? row.facilities.map(normalizeFacility) : [],
    upiNumber: row.upiNumber || "",
    upiScannerUrl: row.upiScannerUrl || "",
    bankAccountHolder: row.bankAccountHolder || "",
    bankAccountNumber: row.bankAccountNumber || "",
    bankIfsc: row.bankIfsc || "",
    bankName: row.bankName || "",
    bankBranch: row.bankBranch || "",
    displayOrder: row.displayOrder ?? 0,
    active: row.active !== false,
    stayType: row.stayType || "",
    iotripsPick: row.iotripsPick === true,
    minGuestsPerRoom: row.minGuestsPerRoom === true,
    roomCategories: Array.isArray(row.roomCategories)
      ? row.roomCategories.map(normalizeCategory)
      : [],
  };
}

function normalizeFacility(row = {}) {
  return {
    ...row,
    name: row.name || "",
    displayOrder: row.displayOrder ?? 0,
    active: row.active !== false,
    optionalWithCost: row.optionalWithCost === true,
    costBehavior: row.costBehavior || facilityCostBehavior(row),
    pricingType: row.pricingType || "PER_ACTIVITY",
    // The customer price of an optional paid facility may be left blank (charged at the payable).
    extraCost: isBlankAmount(row.extraCost) ? null : Number(row.extraCost),
    hotelPayable: payableOf(row.hotelPayable, row.extraCost),
    externalVendorId: row.externalVendorId || row.external_vendor_id || "",
    externalVendorName: row.externalVendorName || row.external_vendor_name || "",
    externalVendorCity: row.externalVendorCity || row.external_vendor_city || "",
    externalVendorType: row.externalVendorType || row.external_vendor_type || "",
  };
}

function normalizeStayType(row = {}) {
  return {
    ...row,
    name: row.name || "",
    description: row.description || "",
    displayOrder: row.displayOrder ?? 0,
    active: row.active !== false,
  };
}

function normalizeCategory(row = {}) {
  return {
    ...row,
    categoryName: row.categoryName || "",
    pricingType: row.pricingType || "PER_ROOM",
    perPersonPrice: row.perPersonPrice ?? 0,
    extraMattressPayable: payableOf(row.extraMattressPayable, row.extraMattressPrice),
    extraMattressPrice: isBlankAmount(row.extraMattressPrice) ? null : row.extraMattressPrice,
    kidRoomCountAgeThreshold: row.kidRoomCountAgeThreshold ?? 3,
    kidGracePerRoom: row.kidGracePerRoom ?? 2,
    maximumUnits: row.maximumUnits ?? 0,
    displayOrder: row.displayOrder ?? 0,
    active: row.active !== false,
    priceOptions: Array.isArray(row.priceOptions)
      ? row.priceOptions.map(normalizePriceOption)
      : [],
    specialPrices: Array.isArray(row.specialPrices)
      ? row.specialPrices.map(normalizeSpecialPrice)
      : [],
    images: Array.isArray(row.images)
      ? row.images.map(normalizeRoomImage)
      : [],
    rooms: Array.isArray(row.rooms)
      ? [...row.rooms]
          .sort((a, b) => Number(a?.displayOrder ?? 0) - Number(b?.displayOrder ?? 0))
          .map(normalizeRoomUnit)
      : [],
  };
}

function normalizeRoomUnit(row = {}, index = 0) {
  return {
    ...row,
    roomNumber: row.roomNumber || "",
    // Rank follows list position so reordering never leaves two rooms claiming the same rank.
    displayOrder: index + 1,
    active: row.active !== false,
    note: row.note || "",
  };
}

function specialPriceGroups(rows = []) {
  const groups = [];
  const byKey = new Map();
  rows.forEach((row, rowIndex) => {
    const rule = {
      recurrenceType: row.recurrenceType || "DATE_RANGE",
      fromDate: row.fromDate || "",
      toDate: row.toDate || "",
      dayOfWeekMask: Number(row.dayOfWeekMask || 0),
      note: row.note || "",
      active: row.active !== false,
    };
    const key = [
      rule.recurrenceType,
      rule.fromDate,
      rule.toDate,
      rule.dayOfWeekMask,
      rule.note,
      rule.active ? "1" : "0",
    ].join("|");
    if (!byKey.has(key)) {
      const group = {
        key,
        rule,
        rowIndexes: [],
        prices: [],
      };
      byKey.set(key, group);
      groups.push(group);
    }
    const group = byKey.get(key);
    group.rowIndexes.push(rowIndex);
    group.prices.push({ rowIndex, row });
  });
  return groups;
}

function normalizePriceOption(row = {}) {
  return {
    ...row,
    memberCount: row.memberCount ?? 2,
    hotelPayable: payableOf(row.hotelPayable, row.cost),
    cost: isBlankAmount(row.cost) ? null : row.cost,
    displayOrder: row.displayOrder ?? 0,
  };
}

function normalizeSpecialPrice(row = {}) {
  const memberCount = row.memberCount === "" || row.memberCount == null ? null : Number(row.memberCount);
  return {
    ...row,
    fromDate: row.fromDate || "",
    toDate: row.toDate || "",
    recurrenceType: row.recurrenceType || "DATE_RANGE",
    dayOfWeekMask: Number(row.dayOfWeekMask || 0),
    memberCount,
    hotelPayable: payableOf(row.hotelPayable, row.cost),
    cost: isBlankAmount(row.cost) ? null : row.cost,
    note: row.note || "",
    active: row.active !== false,
  };
}

function normalizeRoomImage(row = {}) {
  return {
    ...row,
    imageUrl: row.imageUrl || "",
    mediaType: row.mediaType === "VIDEO" ? "VIDEO" : "IMAGE",
    displayOrder: row.displayOrder ?? 0,
    active: row.active !== false,
  };
}
